import { ISelectOption } from '@pinpointhq/thumbtack';

export type TCondition = { operator: string; value: string };

export interface IQuestionDetails {
  index: string | number;
  questionId: string;
  questionType: string;
  title: string;
  additionalInformation: string;
  required: boolean;
  structuredSectionId?: string;
}

export interface IQuestionAnswerUpdateEventDetails {
  questionType: string;
  value: string | number | boolean | ISelectOption[];
  maxNumAnswerOptions?: number;
}

export interface IAnswerMatchParams {
  questionType: string;
  parentQuestionCondition: TCondition;
  value: string | number | boolean | ISelectOption[];
  maxNumAnswerOptions?: number;
}

export function matchQuestionConditions(matchParams: IAnswerMatchParams) {
  const { questionType, parentQuestionCondition, value, maxNumAnswerOptions } = matchParams;
  switch (questionType) {
    case 'boolean':
      return matchBooleanCondition(value as boolean, parentQuestionCondition);
    case 'long_text':
    case 'short_text':
    case 'url':
      return matchTextCondition(value as string, parentQuestionCondition);
    case 'date':
      return matchDateCondition(value as number, parentQuestionCondition);
    case 'multiple_choice':
      return maxNumAnswerOptions === 1
        ? matchSingleSelectCondition(value as string, parentQuestionCondition)
        : matchMultiSelectCondition(value as ISelectOption[], parentQuestionCondition);
    case 'document':
      return matchDocumentCondition(value as boolean, parentQuestionCondition);
    case 'number':
      return matchNumberCondition(value as number, parentQuestionCondition);
    case 'multi_document':
      return matchDocumentCondition(value as boolean, parentQuestionCondition);
    default:
      return false;
  }
}

function matchBooleanCondition(compareValue: boolean, condition: { operator: string; value: string }) {
  if (!condition) return false;

  if (condition?.operator === 'equals' && condition?.value === 'true') {
    return compareValue === true;
  }
  if (condition?.operator === 'not_equals' && condition?.value === 'true') {
    return compareValue !== true;
  }

  if (condition?.operator === 'equals' && condition?.value === 'false') {
    return compareValue === false;
  }
  if (condition?.operator === 'not_equals' && condition?.value === 'false') {
    return compareValue !== false;
  }
  return false;
}

function matchTextCondition(compareValue: string, condition: { operator: string; value: string }) {
  if (!condition) return false;
  if (condition?.operator === 'equals') {
    return compareValue === condition?.value;
  }
  if (condition?.operator === 'not_equals') {
    return compareValue !== condition?.value;
  }
  if (condition?.operator === 'contains') {
    return compareValue.includes(condition?.value);
  }
  if (condition?.operator === 'not_contains') {
    return !compareValue.includes(condition?.value);
  }
  return false;
}

function matchDateCondition(compareValue: number, condition) {
  if (!condition) return false;

  const conditionValue = new Date(condition?.value).setHours(0, 0, 0, 0);

  if (condition?.operator === 'equals') {
    return compareValue === conditionValue;
  }
  if (condition?.operator === 'not_equals') {
    return compareValue !== conditionValue;
  }
  if (condition?.operator === 'greater_than') {
    return compareValue > conditionValue;
  }
  if (condition?.operator === 'less_than') {
    return compareValue < conditionValue;
  }
  return false;
}

function matchSingleSelectCondition(compareValue: string, condition: { operator: string; value: string }) {
  if (!condition) return false;

  if (condition?.operator === 'equals') {
    return compareValue === condition?.value;
  }
  if (condition?.operator === 'not_equals') {
    return compareValue !== condition?.value;
  }
  return false;
}

function matchMultiSelectCondition(compareValue: ISelectOption[], condition) {
  if (!condition) return false;

  if (condition?.operator === 'equals') {
    return compareValue?.map((option) => option.value).includes(condition?.value);
  }
  if (condition?.operator === 'not_equals') {
    return !compareValue?.map((option) => option.value).includes(condition?.value);
  }
  return false;
}

function matchDocumentCondition(compareValue: boolean, condition: { operator: string; value: string }) {
  if (!condition) return false;

  if (condition?.operator === 'equals') {
    return !!compareValue;
  }
  if (condition?.operator === 'not_equals') {
    return !compareValue;
  }
  return false;
}

function matchNumberCondition(compareValue: number, condition) {
  if (!condition) return false;

  if (condition?.operator === 'equals') {
    return compareValue === condition?.value;
  }
  if (condition?.operator === 'not_equals') {
    return compareValue !== condition?.value;
  }
  if (condition?.operator === 'greater_than') {
    return compareValue > condition?.value;
  }
  if (condition?.operator === 'less_than') {
    return compareValue < condition?.value;
  }
  return false;
}
